import { getPartnerId, getUA } from 'core/singleton'

import { LOCATION } from 'core/ga/enums'
import { Location } from 'history'
import { ONLY_POST_MSG_PARTNERS } from 'core/contants/bridge'
import i18n from 'core/i18n'
import { FlattenSimpleInterpolation } from 'styled-components'
import { bridgeParams } from '@types'
import { addMarketingParams } from './url'

export const getLocation = <T>(location: Location<T>) => {
  let LocationKey = ''

  switch (location.pathname) {
    case '/':
      LocationKey = LOCATION.MAIN
      break
    case '/search':
      LocationKey = LOCATION.SEARCH
      break
    case '/curation':
      LocationKey = LOCATION.CURATION
      break
    case '/schedule':
      LocationKey = LOCATION.SCHEDULE
      break

    default:
      if (location?.pathname.includes('/search/')) {
        LocationKey = LOCATION.SEARCH_RESULT
      }
      if (location?.pathname.includes('/curation/')) {
        const _pathname = location.pathname.split('/')
        LocationKey = _pathname[_pathname.length - 2]
      }
      break
  }
  return LocationKey
}

export const msToTime = (s: string | undefined) => {
  if (typeof s === 'undefined') return
  let _s = Math.floor(Number(s))
  function pad(n: number, z: number) {
    z = z || 2
    return ('00' + n).slice(-z)
  }

  const secs = _s % 60
  _s = (_s - secs) / 60
  const mins = _s % 60
  const hrs = (_s - mins) / 60

  return pad(hrs, 2) + ':' + pad(mins, 2) + ':' + pad(secs, 2)
}

export const pvCountToString = (count: number) => {
  let calcCount = count / 1000
  let printPVCount = '0'

  if (calcCount >= 10) {
    const isKr = ['ko', 'ko-kr'].includes(window.navigator.language.toLowerCase())

    calcCount = isKr ? count / 10000 : count / 1000
    printPVCount = i18n.t('number_10000', { number: calcCount.toFixed(1) })
  } else if (calcCount >= 1) {
    printPVCount = i18n.t('number_1000', { number: calcCount.toFixed(1) })
  } else {
    printPVCount = String(count)
  }

  return printPVCount
}

export const getHourWord = (targetDate: Date) => {
  let word = ''

  const today = new Date()
  const tomorrow = new Date(new Date().setDate(new Date().getDate() + 1))
  const targetHour = targetDate.getHours()

  if (targetDate.getMonth() === today.getMonth() && targetDate.getDate() === today.getDate()) {
    word += i18n.t('today')
  } else if (
    targetDate.getMonth() === tomorrow.getMonth() &&
    targetDate.getDate() === tomorrow.getDate()
  ) {
    word += i18n.t('tomorrow')
  }

  if (word) {
    if (1 <= targetHour && targetHour < 12) {
      word += i18n.t('am')
    } else if (12 <= targetHour && targetHour < 13) {
      word += i18n.t('daytime')
    } else if (13 <= targetHour && targetHour < 18) {
      word += i18n.t('pm')
    } else if (18 <= targetHour && targetHour < 19) {
      word += i18n.t('evening')
    } else {
      word += i18n.t('nighttime')
      if (targetHour === 0) word = i18n.t('tonight')
    }
  }

  return word
}

export const dateParser = (_date: string) => {
  const recieveDate = new Date(_date)
  const [month, date, hours, minutes] = [
    recieveDate.getMonth() + 1,
    recieveDate.getDate(),
    recieveDate.getHours(),
    recieveDate.getMinutes(),
  ]

  const hourWord = getHourWord(recieveDate)
  const hour = hours
  let remainHour = hour === 0 || hour === 12 ? 12 : hour % 12

  if (hourWord) {
    if (minutes === 0) {
      return `${hourWord} ${i18n.t('time_hour', { hour: remainHour })}`
    }
    return `${hourWord} ${i18n.t('time_hour_minute', { hour: remainHour, minutes })}`
  }
  const ampam = hours >= 12 ? i18n.t('pm') : i18n.t('am')
  remainHour = hour === 12 ? hour : hour % 12

  if (minutes === 0) {
    return i18n.t('time_non_minute', { month, date, ampam, hour: remainHour })
  }
  return i18n.t('time', { month, date, ampam, hour: remainHour, minutes })
}

export const requestBridgePostMsg = (key: string, params?: object) => {
  const { isIframe, isWebview } = getUA()
  const hasAosBridge = isWebview && window.sauceflex && window.sauceflex[key]
  const hasIosBridge = isWebview && window.webkit && window.webkit.messageHandlers[key]
  const hasSDKAndroid = isWebview && window[key] && window[key][key]

  if (hasSDKAndroid) {
    const payload = JSON.stringify(params) ?? null
    payload ? window[key][key](payload) : window[key][key]()
    return true
  }

  if (hasAosBridge) {
    /* AOS */
    const payload = JSON.stringify(params) ?? null
    payload ? window.sauceflex?.[key]?.(payload) : window.sauceflex?.[key]?.()
    return true
  }

  if (hasIosBridge) {
    /* IOS */
    window.webkit.messageHandlers?.[key]?.postMessage(JSON.stringify(params))
    return true
  }

  if (isIframe) {
    window.parent.postMessage(JSON.stringify({ key, params }), '*')
    return true
  }

  if (window[key]) {
    /** 외부에서 함수가 key에 맞게 정의되어있으면 실행됨 */
    window[key]?.(params)
    return true
  }
  return false
}

export const requestBridge = (key: string, params?: bridgeParams) => {
  const { isIframe, isWebview, isSDKAndroid, isSDKIOS } = getUA()
  const hasAosBridge = isWebview && window.sauceflex && window.sauceflex[key]
  const hasIosBridge = isWebview && window.webkit && window.webkit.messageHandlers[key]
  const hasSDKAndroid = isWebview && window[key] && window[key][key]
  const partnerId = getPartnerId()

  if (params && 'broadcastUrl' in params && params?.broadcastUrl) {
    params.broadcastUrl = addMarketingParams(params.broadcastUrl)
  }
  if (params && 'productUrl' in params && params?.productUrl) {
    params.broadcastUrl = addMarketingParams(params.productUrl)
  }

  if (hasSDKAndroid || isSDKAndroid) {
    const payload = JSON.stringify(params) ?? null
    payload ? window[key][key](payload) : window[key][key]()
    return true
  }

  if (ONLY_POST_MSG_PARTNERS.includes(partnerId)) {
    return requestBridgePostMsg(key, params)
  }

  if (hasAosBridge) {
    /* AOS */
    const payload = JSON.stringify(params) ?? null
    payload ? window.sauceflex?.[key]?.(payload) : window.sauceflex?.[key]?.()
    return true
  }

  if (hasIosBridge || isSDKIOS) {
    /* IOS */
    window.webkit.messageHandlers?.[key]?.postMessage(JSON.stringify(params))
    return true
  }

  /* Iframe */
  if (isIframe) {
    window.parent.postMessage(JSON.stringify({ key, params }), '*')
    return true
  }

  if (window[key]) {
    /** 외부에서 함수가 key에 맞게 정의되어있으면 실행됨 */
    window[key]?.(params)
    return true
  }

  return false
}

/**
 * @function throttle
 */
export const throttle = <P extends unknown[], R, T extends (...args: P) => R>(
  cb: (...args: P) => R,
  sec: number,
): T => {
  let check: NodeJS.Timeout | number
  return ((...args: P) => {
    if (!check) {
      check = setTimeout(() => {
        cb(...args)
        check = 0
      }, sec)
    }
  }) as T
}

/**
 * @function sleep
 */
export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * @const layoutCustom
 * 개별 컴포넌트의 커스텀 반응형 처리를 위한 함수
 */
export const layoutCustom = (size: number, value: FlattenSimpleInterpolation) => {
  return `@media (max-width: ${size}px){${value}}`
}

/**
 * @const layoutSmall
 * 개별 컴포넌트의 반응형 처리를 위한 함수
 * max-width: 768px
 */
export const layoutSmall = (props: FlattenSimpleInterpolation) => {
  return `@media (max-width: 768px){${props}}`
}
/**
 * @name replaceNullWithObject
 * 같은 객체의 값 중 null 이나 값이 비어있을 경우 값을 defaultObject 값으로 대체
 */
export function replaceNullWithObject<T>(serverObject: T, defaultObject: T): T {
  const copiedObject = { ...serverObject }

  for (const key in copiedObject) {
    if (copiedObject[key] === null || copiedObject[key] === undefined) {
      if (Object.prototype.hasOwnProperty.call(defaultObject, key)) {
        copiedObject[key] = defaultObject[key]
      }
    } else if (typeof copiedObject[key] === 'object' && defaultObject[key] !== undefined) {
      copiedObject[key] = replaceNullWithObject(copiedObject[key], defaultObject[key])
    }
  }

  return copiedObject
}

export const timeParser = (_date: string) => {
  const recieveDate = new Date(_date)
  const hour = recieveDate.getHours()
  const minutes = recieveDate.getMinutes()

  let formattedHour = `${hour}`

  if (hour === 0) formattedHour = '00'
  if (hour > 0 && hour <= 9) formattedHour = `0${hour}`
  if (hour > 9) formattedHour = `${hour}`

  if (minutes === 0) return `${formattedHour}:00`
  if (minutes < 10) return `${formattedHour}:0${minutes}`
  return `${formattedHour}:${minutes}`
}
